import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Container } from "reactstrap";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "../../styles/brand-auth.css";

import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import useReveal from "hooks/useReveal";
import { LEGAL_PATHS, LEGAL_STATUS, LEGAL_TERMS_VERSION } from "config/legal";
import { docLangParam, englishLegalDoc, headingIds, loadLegalDoc, slugify } from "legal/loadDoc";
import { withoutReviewNotes } from "legal/publicText";
import "./LegalPage.css";

/* /terms, /privacy and /refunds.

   Public on purpose: sign-up and checkout link here in a new tab, so these
   pages must open for anyone, signed in or not.

   The texts are in src/legal/, in English and Spanish (legal/loadDoc.js).
   The document's language is ?doc=es|en when the address has it, else the
   site language. ?doc never changes the site language (?lng=), so a link from
   the English site can open the Spanish contract. A button switches the
   document only. While LEGAL_STATUS is "draft" (config/legal.js) a banner
   says the text is still under legal review.

   ## and ### headings get stable ids, so deep links such as
   /privacy?doc=es#aviso-de-privacidad-integral work.

   Same navy band and light panel as the profile page (styles/brand-auth.css). */

const TITLES = { terms: "legal.links.terms", privacy: "legal.links.privacy", refunds: "legal.links.refunds" };
const ORDER = ["terms", "privacy", "refunds"];
const SITE = "https://codenec.ai";

// Links to our own site stay in this tab, as a path, so they also work on a
// preview or local build. Everything else opens a new tab.
export function LegalAnchor({ node, href = "", children, ...props }) {
  if (href === SITE || href.startsWith(`${SITE}/`)) {
    const url = new URL(href);
    return <a href={url.pathname + url.search + url.hash} {...props}>{children}</a>;
  }
  if (href.startsWith("mailto:")) return <a href={href} {...props}>{children}</a>;
  return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
}

// Wide tables scroll on their own instead of pushing the page sideways.
function ScrollTable({ node, ...props }) {
  return (
    <div className="cn-legal-table">
      <table {...props} />
    </div>
  );
}

const textOf = (children) =>
  React.Children.toArray(children)
    .map((child) => {
      if (typeof child === "string" || typeof child === "number") return String(child);
      return child && child.props ? textOf(child.props.children) : "";
    })
    .join("");

// h2 / h3 with the id computed for their line in the text (legal/loadDoc.js).
function headingWithId(Tag, ids) {
  return function Heading({ node, children, ...props }) {
    const line = node && node.position && node.position.start ? node.position.start.line : null;
    const id = (line && ids.get(line)) || slugify(textOf(children));
    return <Tag id={id} {...props}>{children}</Tag>;
  };
}

const LegalPage = ({ doc }) => {
  // The panel's entrance — see styles/premium.css.
  useReveal();
  const { t, i18n } = useTranslation();
  const { search, hash } = useLocation();
  const key = TITLES[doc] ? doc : "terms";
  const siteLang = (i18n.language || "es").startsWith("en") ? "en" : "es";
  const requested = docLangParam(search) || siteLang;

  // The toggle's choice holds only for the page it was made on.
  const page = `${key}${search}`;
  const [choice, setChoice] = useState({ page: null, lang: null });
  const docLang = choice.page === page ? choice.lang : requested;

  // English is there at once; Spanish arrives from its own chunk.
  const [loaded, setLoaded] = useState(null);
  const wanted = `${key}:${docLang}`;
  const shown = docLang === "en" ? englishLegalDoc(key) : loaded && loaded.wanted === wanted ? loaded : null;

  useEffect(() => {
    if (docLang === "en") return undefined;
    let live = true;
    loadLegalDoc(key, docLang).then((result) => {
      if (live) setLoaded({ ...result, wanted: `${key}:${docLang}` });
    });
    return () => {
      live = false;
    };
  }, [key, docLang]);

  // The [LAWYER: ...] and [OWNER: ...] review notes are for the lawyer, not the
  // public: they are cut here (legal/publicText.js).
  const source = shown ? shown.markdown : null;
  const markdown = useMemo(() => (source ? withoutReviewNotes(source) : ""), [source]);
  const components = useMemo(() => {
    const ids = headingIds(markdown);
    return { a: LegalAnchor, table: ScrollTable, h2: headingWithId("h2", ids), h3: headingWithId("h3", ids) };
  }, [markdown]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }, [key]);

  // A #heading in the address: go there once its text is on the page. The
  // browser's own jump happens before a Spanish text has loaded. Once only, so
  // switching the language later does not jump again.
  const jumped = useRef(null);
  useEffect(() => {
    const target = `${page}${hash}`;
    if (!markdown || !hash || jumped.current === target) return;
    let id = hash.slice(1);
    try {
      id = decodeURIComponent(id);
    } catch (err) {
      // Keep it as written.
    }
    const heading = id ? document.getElementById(id) : null;
    if (!heading) return;
    jumped.current = target;
    if (typeof heading.scrollIntoView === "function") heading.scrollIntoView();
  }, [markdown, page, hash]);

  const otherLang = shown && shown.lang === "es" ? "en" : "es";
  // Related links keep a document language that differs from the site's.
  const relatedQuery = shown && shown.lang !== siteLang ? `?doc=${shown.lang}` : "";

  return (
    <>
      <DemoNavbar />
      <main className="cn-cover-page cn-legal">
        <section className="cn-cover cn-grid">
          <span className="cn-grain" aria-hidden="true" />
          <span className="cn-orb cn-orb-1" />
          <span className="cn-orb cn-orb-2" />
          <span className="cn-orb cn-orb-3" />
        </section>

        <Container className="cn-legal-shell">
          <div className="cn-panel cn-legal-panel" data-reveal>
            <h1 className="cn-panel-title">{t(TITLES[key])}</h1>
            <p className="cn-panel-sub">{t("legal.version", { version: LEGAL_TERMS_VERSION })}</p>

            {LEGAL_STATUS === "draft" && (
              <div className="cn-legal-draft" role="note">
                <strong>{t("legal.draftTitle")}</strong> {t("legal.draftBody")}
              </div>
            )}

            {LEGAL_STATUS === "draft" && shown && shown.lang === "es" && (
              <p className="cn-legal-lang">
                {t("legal.spanishDraftNote", { defaultValue: "This Spanish version is a draft under legal review." })}
              </p>
            )}

            {shown && shown.englishOnly && <p className="cn-legal-lang">{t("legal.englishOnly")}</p>}

            {shown && !shown.englishOnly && (
              <div className="cn-legal-tools">
                <button
                  type="button"
                  className="cn-legal-toggle"
                  onClick={() => setChoice({ page, lang: otherLang })}
                >
                  {otherLang === "en"
                    ? t("legal.readInEnglish", { defaultValue: "Read in English" })
                    : t("legal.readInSpanish", { defaultValue: "Read in Spanish" })}
                </button>
              </div>
            )}

            <p className="cn-legal-status" role="status">
              {shown ? "" : t("legal.loadingDoc", { defaultValue: "Loading…" })}
            </p>

            {shown && (
              <article className="cn-legal-body" lang={shown.lang}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                  {markdown}
                </ReactMarkdown>
              </article>
            )}

            <nav className="cn-legal-related" aria-label={t("legal.related")}>
              {ORDER.filter((other) => other !== key).map((other) => (
                <a key={other} href={`${LEGAL_PATHS[other]}${relatedQuery}`}>
                  {t(`legal.links.${other}`)}
                </a>
              ))}
            </nav>
          </div>
        </Container>
      </main>
      <MainFooter />
    </>
  );
};

export default LegalPage;
